import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    VIEW, EDIT,
    DATE_FORMAT,
    TODAY, FIRST_DAY_OF_MONTH, LAST_DAY_OF_MONTH, SPECIFIC_DATE,
    ONE_QUARTER, HALF_YEAR, ONE_YEAR,
    WEEKLY, BIWEEKLY, MONTHLY,
    MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY,
    OPEN, IN_PROGRESS, ARCHIVED,
    STAY_ABOVE, STAY_BELOW, HARD, HARD_SOFT,
} from "const"
import { OkrSession, OkrSessionCreate, OkrKeyResult } from "server/models"
import { okrSessionsRoute } from "client/parts/routes"
import {
    StateType,
    OkrCadenceUpdateType,
    forceRerenderContent,
    replaceHistoryState,
} from "client/utils"
import {
    StoreType,
    OkrKeyResultList,
    OkrSessionValidation,

    LoadAllOkrKeyResults,
    SaveOkrSession,
    UpdateOkrKeyResultSoftDeadline,
    UpdateOkrKeyResultHardDeadline,

    setOkrSessionEdit,
    setOkrSessionReloadList,
    setOkrSessionReloadView,
    setOkrSessionReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    DatePicker,
    SelectDropdown,
    SelectDropdownSearch,
    SelectDropdownItem,
    UserDropdown,
    DateFormatter,
    Empty,
    Help,
    ColorPicker,
    Button,
    SpinnerIcon,
} from "client/components"
import {
    SessionTitleWrapper,
    SessionDurationWrapper,
    SessionDurationDelimeter,
    SessionStatusWrapper,
    SessionStatusDescription,
} from "../styled"

import ConfirmDeadline from "./confirmDeadline"


const weekCadenceUpdate: OkrCadenceUpdateType[] = [MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY]
const monthCadenceUpdate: OkrCadenceUpdateType[] = [FIRST_DAY_OF_MONTH, LAST_DAY_OF_MONTH, SPECIFIC_DATE]

const daysArray: number[] = []
for (let i = 1; i <= 28; i++) {
    daysArray.push(i)
}


let isMounted: boolean
let mode: number

interface EditOkrSessionProps {
    session: OkrSession
}

const Form = ({
    session,
}: EditOkrSessionProps) => {
    const availableSessions = useSelector((state: StoreType) => state.okrSession.availableSessions)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [parentSession, setParentSession] = useState(session.session || null)
    const [sessionSearch, setSessionSearch] = useState("")
    const [sessionsFiltered, setSessionsFiltered] = useState<OkrSession[]>([])

    const [keyResultsSoftConflict, setKeyResultsSoftConflict] = useState<OkrKeyResult[]>([])
    const [keyResultsHardConflict, setKeyResultsHardConflict] = useState<OkrKeyResult[]>([])
    const [keyResultsAllConflict, setKeyResultsAllConflict] = useState<OkrKeyResult[]>([])

    const [name, setName] = useState(session.name)
    const [dateStart, setDateStart] = useState(session.dateStart ? new Date(session.dateStart) : null)
    const [dateEnd, setDateEnd] = useState(session.dateEnd ? new Date(session.dateEnd) : null)
    const [cadence, setCadence] = useState(session.cadence)
    const [cadenceUpdate, setCadenceUpdate] = useState(session.cadenceUpdate)
    const [cadenceUpdateDay, setCadenceUpdateDay] = useState(session.cadenceUpdateDay)
    const [owner, setOwner] = useState(session.owner || userData.userId)
    const [color, setColor] = useState(session.color)
    const [status, setStatus] = useState(session.status)

    const [showConfirm, setShowConfirm] = useState(false)
    const [validation, setValidation] = useState<OkrSessionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    const maxDeadlineSoft = new Date(dateEnd)
    maxDeadlineSoft.setDate(maxDeadlineSoft.getDate() - 1)

    const minDeadlineHard = new Date(dateStart)
    minDeadlineHard.setDate(minDeadlineHard.getDate() + 1)

    useEffect(() => {
        isMounted = true
        if (!session.id) {
            const {
                defaultSessionStartDate,
                defaultSessionDuration,
                defaultSessionCadence,
            } = accountSettings.okr

            // date start
            const start = new Date()
            if (defaultSessionStartDate === TODAY) { /* without change */ }
            if (defaultSessionStartDate === FIRST_DAY_OF_MONTH) start.setDate(1)
            setDateStart(start)

            // date end
            const end = new Date(start)
            if (defaultSessionDuration === ONE_QUARTER) end.setMonth(end.getMonth() + 3)
            if (defaultSessionDuration === HALF_YEAR) end.setMonth(end.getMonth() + 6)
            if (defaultSessionDuration === ONE_YEAR) end.setFullYear(end.getFullYear() + 1)
            setDateEnd(end)

            // cadence
            setCadence(defaultSessionCadence)
            if (defaultSessionCadence === WEEKLY || defaultSessionCadence === BIWEEKLY) setCadenceUpdate(MONDAY)
            if (defaultSessionCadence === MONTHLY) setCadenceUpdate(FIRST_DAY_OF_MONTH)

            // name
            let quarter: number
            const currentMonth = start.getMonth()
            const currentYear = start.getFullYear()
            if (currentMonth >= 0 && currentMonth <= 2) quarter = 1
            if (currentMonth >= 3 && currentMonth <= 5) quarter = 2
            if (currentMonth >= 6 && currentMonth <= 8) quarter = 3
            if (currentMonth >= 9 && currentMonth <= 11) quarter = 4
            setName(`Q${quarter} - ${currentYear}`)
        }
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const filtered = availableSessions.filter((row) => (
            row.name.toUpperCase().indexOf(sessionSearch.trim().toUpperCase()) >= 0
        ))
        setSessionsFiltered(filtered)
    }, [availableSessions, sessionSearch])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = session.id
            ? `${okrSessionsRoute}/${VIEW}/${session.id}`
            : okrSessionsRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = () => {
        if (dateEnd < dateStart) {
            setValidation({ dateStart: lang.dateEndLessDateStart })
            return
        }
        if (
            dateEnd.getDate() === dateStart.getDate()
            && dateEnd.getMonth() === dateStart.getMonth()
            && dateEnd.getFullYear() === dateStart.getFullYear()
        ) {
            setValidation({ dateStart: lang.dateEndEqualDateStart })
            return
        }

        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        LoadAllOkrKeyResults(session.id).then((res) => {
            const softConflict: OkrKeyResult[] = []
            const hardConflict: OkrKeyResult[] = []
            const krList: OkrKeyResultList = {}

            res.keyResults.forEach((keyResult) => {
                if (keyResult.type !== STAY_ABOVE && keyResult.type !== STAY_BELOW) {
                    if (keyResult.deadlineType === HARD_SOFT) {
                        const softDeadline = new Date(keyResult.deadlineSoft)
                        if (softDeadline < dateStart || softDeadline > maxDeadlineSoft) {
                            softConflict.push(keyResult)
                            krList[keyResult.id] = keyResult
                        }
                    }

                    if (keyResult.deadlineType === HARD || keyResult.deadlineType === HARD_SOFT) {
                        const hardDeadline = new Date(keyResult.deadlineHard)
                        if (hardDeadline < minDeadlineHard || hardDeadline > dateEnd) {
                            hardConflict.push(keyResult)
                            krList[keyResult.id] = keyResult
                        }
                    }
                }
            })

            const allConflicts = Object.values(krList)
            setKeyResultsSoftConflict(softConflict)
            setKeyResultsHardConflict(hardConflict)
            setKeyResultsAllConflict(allConflicts)

            if (allConflicts.length > 0) {
                setShowConfirm(true)
                setSaveState(IDLE)
                setApplyState(IDLE)
            } else {
                save()
            }
        })
    }

    const save = () => {
        const newSessionData: OkrSessionCreate = {
            accountId: session.accountId || userData.accountId,
            sessionId: parentSession ? parentSession.id : null,
            name,
            dateStart,
            dateEnd,
            cadence,
            cadenceUpdate,
            cadenceUpdateDay: cadenceUpdate === SPECIFIC_DATE ? cadenceUpdateDay : null,
            color,
            owner,
            status,
        }

        SaveOkrSession(
            session.id,
            newSessionData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newSessionId) replaceHistoryState(`${okrSessionsRoute}/${EDIT}/${res.newSessionId}`)
            }
            if (res.validation) return

            dispatch(setOkrSessionEdit({
                id: res.newSessionId || session.id,
                ...newSessionData,
            }))

            dispatch(setOkrSessionReloadList(false))
            dispatch(setOkrSessionReloadView(false))
            setTimeout(() => {
                dispatch(setOkrSessionReloadList(true))
                dispatch(setOkrSessionReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setOkrSessionReloadEdit(false))
                setTimeout(() => {
                    dispatch(setOkrSessionReloadEdit(true))
                }, 0)
            }
        })
    }

    const onConfirm = () => {
        setShowConfirm(false)
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const promiseHardArray: Promise<any>[] = []
        const promiseSoftArray: Promise<any>[] = []

        keyResultsAllConflict.forEach((keyResult) => {
            let newHardDeadline: Date
            let newSoftDeadline: Date

            if (keyResult.deadlineType === HARD || keyResult.deadlineType === HARD_SOFT) {
                const hardDeadline = new Date(keyResult.deadlineHard)
                if (hardDeadline < minDeadlineHard || hardDeadline > dateEnd) {
                    newHardDeadline = new Date(dateEnd)
                }

                if (keyResult.deadlineType === HARD_SOFT) {
                    const softDeadline = new Date(keyResult.deadlineSoft)
                    if (softDeadline < dateStart || softDeadline > maxDeadlineSoft) {
                        newSoftDeadline = new Date(newHardDeadline || hardDeadline)
                        newSoftDeadline.setDate(newSoftDeadline.getDate() - 1)
                    }
                }
            }

            if (newHardDeadline) {
                promiseHardArray.push(
                    UpdateOkrKeyResultHardDeadline({
                        id: keyResult.id,
                        deadline: newHardDeadline,
                    }),
                )
            }
            if (newSoftDeadline) {
                promiseSoftArray.push(
                    UpdateOkrKeyResultSoftDeadline({
                        id: keyResult.id,
                        deadline: newSoftDeadline,
                    }),
                )
            }
        })

        Promise.all(promiseHardArray).then(() => (
            Promise.all(promiseSoftArray).then(() => (
                save()
            ))
        ))
    }

    let cadenceUpdateList: OkrCadenceUpdateType[] = []
    if (cadence === WEEKLY || cadence === BIWEEKLY) cadenceUpdateList = weekCadenceUpdate
    if (cadence === MONTHLY) cadenceUpdateList = monthCadenceUpdate

    let cadenceUpdateTitle = ""
    if (cadenceUpdate) {
        if (cadence === WEEKLY) cadenceUpdateTitle = lang.everyDayX.replace("{0}", lang[cadenceUpdate])
        if (cadence === BIWEEKLY) cadenceUpdateTitle = lang.onDayX.replace("{0}", lang[cadenceUpdate])
        if (cadence === MONTHLY) cadenceUpdateTitle = lang[cadenceUpdate]
    }

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.timeframe}</Label>
                        <FormGroup inline>
                            <DatePicker
                                selected={dateStart}
                                onChange={(date: Date) => {
                                    if (!date) setDateStart(dateStart)
                                    else setDateStart(date)
                                }}
                                dateFormat={DATE_FORMAT}
                            />
                            <DatePicker
                                selected={dateEnd}
                                onChange={(date: Date) => {
                                    if (!date) setDateEnd(dateEnd)
                                    else setDateEnd(date)
                                }}
                                dateFormat={DATE_FORMAT}
                            />
                        </FormGroup>
                        <Feedback>{validation && validation.dateStart}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label flex>
                            {lang.parentSession}
                            <Help text={lang.parentSessionDescription} />
                        </Label>
                        <SelectDropdown
                            fit="true"
                            title={
                                parentSession ? (
                                    <SessionTitleWrapper>
                                        {`${parentSession.name} (${lang[parentSession.status]})`}
                                        <SessionDurationWrapper>
                                            <DateFormatter date={parentSession.dateStart} showTime={false} />
                                            <SessionDurationDelimeter>—</SessionDurationDelimeter>
                                            <DateFormatter date={parentSession.dateEnd} showTime={false} />
                                        </SessionDurationWrapper>
                                    </SessionTitleWrapper>
                                ) : <Empty />
                            }
                        >
                            <SelectDropdownSearch
                                value={sessionSearch}
                                onChange={(e) => setSessionSearch(e.target.value)}
                                onClearSearch={() => setSessionSearch("")}
                            />
                            <SelectDropdownItem onClick={() => setParentSession(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            {
                                sessionsFiltered.map((row) => (
                                    <SelectDropdownItem key={row.id} onClick={() => setParentSession(row)}>
                                        <SessionTitleWrapper>
                                            {`${row.name} (${lang[row.status]})`}
                                            <SessionDurationWrapper>
                                                <DateFormatter date={row.dateStart} showTime={false} />
                                                <SessionDurationDelimeter>—</SessionDurationDelimeter>
                                                <DateFormatter date={row.dateEnd} showTime={false} />
                                            </SessionDurationWrapper>
                                        </SessionTitleWrapper>
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.sessionId}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label flex>
                            {lang.cadence}
                            <Help text={lang.cadenceDescription} />
                        </Label>
                        <SelectDropdown title={cadence ? lang[cadence] : ""}>
                            <SelectDropdownItem onClick={() => {
                                setCadence(WEEKLY)
                                setCadenceUpdate(MONDAY)
                            }}
                            >
                                {lang.weekly}
                            </SelectDropdownItem>
                            <SelectDropdownItem onClick={() => {
                                setCadence(BIWEEKLY)
                                setCadenceUpdate(MONDAY)
                            }}
                            >
                                {lang.biweekly}
                            </SelectDropdownItem>
                            <SelectDropdownItem onClick={() => {
                                setCadence(MONTHLY)
                                setCadenceUpdate(FIRST_DAY_OF_MONTH)
                            }}
                            >
                                {lang.monthly}
                            </SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>
                    <FormGroup>
                        <SelectDropdown title={cadenceUpdateTitle}>
                            {
                                cadenceUpdateList.map((row) => (
                                    <SelectDropdownItem
                                        key={row}
                                        onClick={() => {
                                            setCadenceUpdate(row)
                                            if (row === SPECIFIC_DATE) setCadenceUpdateDay(1)
                                        }}
                                    >
                                        {lang[row]}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                    </FormGroup>
                    {
                        cadenceUpdate === SPECIFIC_DATE && (
                            <FormGroup>
                                <SelectDropdown title={lang.xDayOfMonth.replace("{0}", cadenceUpdateDay.toString())}>
                                    {
                                        daysArray.map((day) => (
                                            <SelectDropdownItem key={day} onClick={() => setCadenceUpdateDay(day)}>
                                                {day}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                    <SelectDropdownItem
                                        title={lang.xDayOfMonthWarning}
                                        style={{ cursor: "not-allowed" }}
                                    >
                                        29, 30, 31
                                    </SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.color}</Label>
                        <ColorPicker
                            selectedColor={color}
                            onChange={setColor}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.owner}</Label>
                        <UserDropdown
                            selectedId={owner}
                            listIds={accountUsers}
                            title={lang.changeOwner}
                            showDelete={false}
                            onChange={(userId) => setOwner(userId)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <SelectDropdown title={lang[status]}>
                            <SelectDropdownItem onClick={() => setStatus(OPEN)}>
                                <SessionStatusWrapper>
                                    <span>{lang.open}</span>
                                    <SessionStatusDescription>{lang.openSessionDescription}</SessionStatusDescription>
                                </SessionStatusWrapper>
                            </SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setStatus(IN_PROGRESS)}>
                                <SessionStatusWrapper>
                                    <span>{lang.inProgress}</span>
                                    <SessionStatusDescription>{lang.inProgressSessionDescription}</SessionStatusDescription>
                                </SessionStatusWrapper>
                            </SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setStatus(ARCHIVED)}>
                                <SessionStatusWrapper>
                                    <span>{lang.archived}</span>
                                    <SessionStatusDescription>{lang.archivedSessionDescription}</SessionStatusDescription>
                                </SessionStatusWrapper>
                            </SelectDropdownItem>
                        </SelectDropdown>
                        <Feedback>{validation && validation.status}</Feedback>
                    </FormGroup>

                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        onClick={() => {
                            mode = 1
                            onSave()
                        }}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        onClick={() => {
                            mode = 2
                            onSave()
                        }}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>

            {
                showConfirm && (
                    <ConfirmDeadline
                        keyResultsSoftConflict={keyResultsSoftConflict}
                        keyResultsHardConflict={keyResultsHardConflict}
                        onConfirm={onConfirm}
                        onCancel={() => setShowConfirm(false)}
                    />
                )
            }
        </PageForm>
    )
}
export default Form
